/**
 * Analytics Module
 * Main entry point for analytics initialization and tracking
 *
 * Usage:
 *   import { initAnalytics, completeAnalyticsSetup } from './analytics/index.js';
 *   import * as eventTracker from './analytics/event-tracker.js';
 *
 *   // In init.js, before auth:
 *   await initAnalytics();
 *
 *   // After auth is complete:
 *   await completeAnalyticsSetup();
 *
 *   // Track events anywhere:
 *   eventTracker.trackFilesLoaded(fileCount, totalDuration);
 */

import { initGA4, getGAClientId } from './ga4.js';
import { initPostHog, getDistinctId } from './posthog.js';
import { init as initCustomAnalytics, trackSession, linkAnalyticsToUser, getSupabaseSessionId } from './custom-analytics.js';
import { captureUTM, getDeviceType } from './utils.js';
import { trackAppLoaded, identifyUser } from './event-tracker.js';
import { VisualizerRegistry } from '../../visualizer.js';
import { authService } from '../../auth/auth-service.js';

let analyticsInitialized = false;
let analyticsSetupComplete = false;

/**
 * Initialize analytics (Phase 1)
 * Call this BEFORE auth initialization to capture PostHog ID before anonymous user creation.
 *
 * This function:
 * 1. Captures UTM parameters from URL
 * 2. Initializes GA4
 * 3. Initializes PostHog (generates distinct ID)
 *
 * Note: Custom analytics (Supabase session tracking) is deferred to completeAnalyticsSetup()
 * because it needs auth context.
 */
export async function initAnalytics() {
  if (analyticsInitialized) {
    console.log('[Analytics] Already initialized');
    return;
  }

  console.log('[Analytics] Initializing...');

  // Step 1: Capture UTM parameters from URL immediately
  captureUTM();

  // Step 2: Initialize GA4 (loads script, sets up gtag)
  initGA4();

  // Step 3: Initialize PostHog (loads script, generates distinct ID)
  // This must happen BEFORE anonymous user creation so the ID can be linked
  initPostHog();

  analyticsInitialized = true;
  console.log('[Analytics] Phase 1 complete (GA4 + PostHog initialized)');
}

/**
 * Complete analytics setup (Phase 2)
 * Call this AFTER auth initialization is complete.
 *
 * This function:
 * 1. Waits for PostHog distinct ID to be ready
 * 2. Tracks session to Supabase
 * 3. Links analytics IDs to user profile (if authenticated)
 * 4. Tracks app_loaded event
 */
export async function completeAnalyticsSetup() {
  if (analyticsSetupComplete) {
    console.log('[Analytics] Setup already complete');
    return;
  }

  console.log('[Analytics] Completing setup...');

  // Initialize custom analytics (tracks session to Supabase)
  // Note: initCustomAnalytics polls for PostHog ID internally via waitForValue
  await initCustomAnalytics();

  // Link analytics IDs to user profile if authenticated
  if (authService.isAuthenticated()) {
    await linkAnalyticsToUser();

    // If not anonymous, identify the user in PostHog
    if (!authService.isAnonymous()) {
      const user = authService.getUser();
      if (user) {
        identifyUser(user.id, {
          email: user.email,
          is_anonymous: false
        });
      }
    }
  }

  // Track app loaded event
  const vizList = VisualizerRegistry.getListSync();
  trackAppLoaded({
    visualizerCount: vizList.length,
    deviceType: getDeviceType()
  });

  analyticsSetupComplete = true;
  console.log('[Analytics] Phase 2 complete (session tracked, user linked)');
}

/**
 * Handle auth state change
 * Call this when auth state changes to update analytics identity
 */
export async function handleAuthStateChange(event, session) {
  if (event === 'SIGNED_IN' && session?.user) {
    // User signed in - link analytics and identify
    await linkAnalyticsToUser();

    if (!session.user.is_anonymous) {
      identifyUser(session.user.id, {
        email: session.user.email,
        is_anonymous: false
      });
    }
  } else if (event === 'SIGNED_OUT') {
    // User signed out - reset PostHog identity
    const { reset } = await import('./posthog.js');
    reset();
  }
}

// Re-export commonly used functions
export { getDistinctId, getOriginalAnonId } from './posthog.js';
export { getGAClientId } from './ga4.js';
export { getSupabaseSessionId } from './custom-analytics.js';
export { getUTMData, getDeviceType } from './utils.js';

// Re-export event tracker for convenience
export * as eventTracker from './event-tracker.js';
